<template>
  <header class="itinerary-header-section">
    <div class="container">
      <h1 class="page-header-title">
        <span class="page-header-title__itinerary-title">{{ itinerary.title }}</span>
      </h1>
    </div>

    <figure class="page-header-media">
      <img v-if="itinerary.media" class="page-header-media__image" :src="itinerary.media.url" :alt="itinerary.title" />

      <div v-if="parsedStartDate && parsedEndDate" class="page-header-media__overlay">
        <div class="page-header-duration-info">
          <div class="itinerary-duration__dates">
            <div class="itinerary-duration-info">
              <span class="itinerary-duration-info__before">{{ parsedStartDate.weekDay }}</span>
              <span class="itinerary-duration-info__value">{{ parsedStartDate.date }}</span>
              <span class="itinerary-duration-info__after">{{ parsedStartDate.year }}</span>
            </div>
            <span class="page-header-duration-info__slash-separator" />
            <div class="itinerary-duration-info">
              <span class="itinerary-duration-info__before">{{ parsedEndDate.weekDay }}</span>
              <span class="itinerary-duration-info__value">{{ parsedEndDate.date }}</span>
              <span class="itinerary-duration-info__after">{{ parsedEndDate.year }}</span>
            </div>
          </div>
          <span class="page-header-duration-info__line-separator" />
          <div class="itinerary-duration__days">
            <div class="itinerary-duration-info">
              <span class="itinerary-duration-info__before">Duration</span>
              <span class="itinerary-duration-info__value">{{ durationInDays }}</span>
              <span class="itinerary-duration-info__after">Days</span>
            </div>
          </div>
        </div>
      </div>
    </figure>
  </header>
</template>

<script>
import { durationInDays, formatDate } from '@/helpers/dateTimeHelper';

export default {
  props: {
    itinerary: {
      type: Object,
      required: true,
    },
  },
  computed: {
    parsedStartDate() {
      if (!this.itinerary.startDate) {
        return null;
      }

      return {
        date: formatDate(this.itinerary.startDate, {
          month: 'short',
          day: '2-digit',
        }),
        weekDay: formatDate(this.itinerary.startDate, {
          weekday: 'long',
        }),
        year: formatDate(this.itinerary.startDate, {
          year: 'numeric',
        }),
      };
    },
    parsedEndDate() {
      if (!this.itinerary.endDate) {
        return null;
      }

      return {
        date: formatDate(this.itinerary.endDate, {
          month: 'short',
          day: '2-digit',
        }),
        weekDay: formatDate(this.itinerary.endDate, {
          weekday: 'long',
        }),
        year: formatDate(this.itinerary.endDate, {
          year: 'numeric',
        }),
      };
    },
    durationInDays() {
      const duration = durationInDays(this.itinerary.startDate, this.itinerary.endDate);

      return duration > 0 ? duration : '< 1';
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables-site/variables' as vars;

.itinerary-header-section {
  .page-header-title {
    margin-bottom: 32px;
    text-align: center;
    text-transform: uppercase;

    &__itinerary-title {
      display: block;
      font-size: 40px;
      font-weight: 700;
      line-height: 52px;
      letter-spacing: 6px;
    }
  }

  .page-header-media {
    position: relative;
    min-height: 160px;
    margin: 0;
    background-color: vars.$c-dark-gray;

    &__image {
      width: 100%;
      height: auto;
      max-height: 600px;
      object-fit: cover;
    }

    &__overlay {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      padding: 48px 16px 20px;
      background: linear-gradient(to bottom, transparent, vars.$c-total-black);
    }

    .page-header-duration-info {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;

      @media (min-width: vars.$mobile-break-point + 1px) {
        gap: 20px;
      }

      &__slash-separator {
        width: 20px;
        height: 20px;
        background: url('~@/assets/images/duration-info-slash-separator.svg') center no-repeat;
      }
      &__line-separator {
        width: 20px;
        height: 84px;
        background: url('~@/assets/images/duration-info-line-separator.svg') center no-repeat;
      }
    }

    .itinerary-duration {
      &__dates {
        display: flex;
        gap: 12px;
        align-items: center;

        @media (min-width: vars.$mobile-break-point + 1px) {
          gap: 20px;
        }
      }

      &__days {
        @media (min-width: vars.$mobile-break-point + 1px) {
          padding-left: 10px;
        }
      }

      &-info {
        text-align: center;

        & > * {
          display: block;
        }

        &__before,
        &__after {
          color: vars.$c-gray-a2;
          font-size: 14px;
          line-height: 24px;

          @media (min-width: vars.$mobile-break-point + 1px) {
            font-size: 16px;
          }
        }

        &__before {
          font-weight: 500;
        }

        &__value {
          color: vars.$c-total-white;
          font-size: 22px;
          font-weight: 500;
          line-height: 36px;

          @media (min-width: vars.$mobile-break-point + 1px) {
            font-size: 24px;
          }
        }
      }
    }
  }
}
</style>
